import React, { useRef, useEffect, useState } from "react";
import {
  motion,
  useInView,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import { sanityClient, urlFor } from "../lib/sanityClient";
import "../styles/HeroSection.css";

const QUERY = `*[_type == "heroSection"][0]{
  tagline, taglineIcon, headlinePart1, headlinePart2,
  headlinePart3, subtext, ctaText, ctaUrl,
  heroImageDesktop, heroImageMobile
}`;



const HeroSection = ({ isSplash }) => {
  const [data, setData] = useState(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    sanityClient.fetch(QUERY).then(setData);
  }, []);

  const mouseX = useMotionValue(0.5);
  const mouseY = useMotionValue(0.5);
  const rotateX = useTransform(mouseY, [0, 1], [15, -15]);
  const rotateY = useTransform(mouseX, [0, 1], [-15, 15]);
  const smoothRotateX = useSpring(rotateX, { stiffness: 120, damping: 15 });
  const smoothRotateY = useSpring(rotateY, { stiffness: 120, damping: 15 });

  const [isDesktop, setIsDesktop] = useState(true);

  useEffect(() => {
    mouseX.set(0.5);
    mouseY.set(0.5);
  }, [mouseX, mouseY]);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 1024); // Desktop if width > 1024px
    };

    handleResize(); // Initial check
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const handleMouseMove = (e) => {
    const bounds = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - bounds.left) / bounds.width;
    const y = (e.clientY - bounds.top) / bounds.height;
    mouseX.set(x);
    mouseY.set(y);
  };

  if (isSplash) return null;

  return (
    <section className="hero" ref={ref}>
      {/* 🌟 Radial Glow Backgrounds */}
      <motion.div
        className="hero-glow-ellipse"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.75 }}
        transition={{ duration: 1.2, ease: "easeOut", delay: 0.3 }}
      />
      <motion.div
        className="hero-glow-bottom-right"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.75 }}
        transition={{ duration: 1.2, ease: "easeOut", delay: 0.5 }}
      />

      <div className="hero-overlay-wrapper">
        {data && (
        <motion.div
          className="hero-content"
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={{
            hidden: {},
            visible: {
              transition: { staggerChildren: 0.15, delayChildren: 0.2 },
            },
          }}
        >
          <motion.p className="tagline" variants={fadeUp}>
            <img
              src={urlFor(data.taglineIcon).url()}
              alt="radio icon"
              className="tagline-icon"
            />
            {data.tagline}
          </motion.p>

          <motion.h1 className="headline" variants={fadeUp}>
            {data.headlinePart1 && <span>{data.headlinePart1}</span>}
            {data.headlinePart1 && <br />}
            {data.headlinePart2 && <span className="highlight">{data.headlinePart2}</span>}
            {data.headlinePart3 && <span>{data.headlinePart3}</span>}
          </motion.h1>

          <motion.p className="subtext" variants={fadeUp} style={{ whiteSpace: "pre-line" }}>
            {data.subtext}
          </motion.p>

          <motion.div className="cta-buttons" variants={fadeUp}>
            <motion.button
              className="get-started"
              onClick={() =>
                window.open(data.ctaUrl, "_blank")
              }
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.95 }}
            >
              {data.ctaText}
              <img
                src="/ArrowIMG.svg"
                alt="arrow icon"
                className="arrow-icon"
              />
            </motion.button>
          </motion.div>
        </motion.div>
        )}

        <div
          className="hero-visual"
          onMouseMove={isDesktop ? handleMouseMove : undefined}
          onMouseLeave={() => {
            mouseX.set(0.5);
            mouseY.set(0.5);
          }}
          style={{
            perspective: 1000,
            transformStyle: "preserve-3d",
          }}
        >
          {data && (isDesktop ? (
            <motion.img
              src={urlFor(data.heroImageDesktop).url()}
              alt="Hero Visual"
              className="hero-image"
              style={{
                rotateX: smoothRotateX,
                rotateY: smoothRotateY,
              }}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.2, ease: "easeOut" }}
              whileHover={{ scale: 1.05 }}
            />
          ) : (
            <img
              src={urlFor(data.heroImageMobile).url()}
              alt="Hero Visual"
              className="hero-image"
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
